import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { IconPlus, IconTrash } from '../components/Icons';

const CATEGORIES = [
  { id: 'Server', label: '💻 Server / Hosting', color: '#3B82F6' },
  { id: 'GoogleMaps', label: '🗺️ Google Maps API', color: '#10B981' },
  { id: 'Marketing', label: '📢 Marketing & Ads', color: '#8B5CF6' },
  { id: 'Salary', label: '🛵 Delivery Salary', color: '#F59E0B' },
  { id: 'Maintenance', label: '🔧 Maintenance', color: '#EF4444' },
  { id: 'Other', label: '📦 Other / इतर', color: '#64748B' },
];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Server');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [filterCat, setFilterCat] = useState('All');

  useEffect(() => {
    // Fetch Expenses
    const qExp = query(collection(db, 'expenses'), orderBy('date', 'desc'));
    const unsubExp = onSnapshot(qExp, snap => {
      setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    // Fetch Orders for Commission calculation
    const qOrd = query(collection(db, 'orders'));
    const unsubOrd = onSnapshot(qOrd, snap => {
      setOrders(snap.docs.map(d => d.data()));
    });

    return () => {
      unsubExp();
      unsubOrd();
    };
  }, []);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!title.trim() || !amount || parseFloat(amount) <= 0) {
      alert('कृपया नाव आणि वैध रक्कम टाका.');
      return;
    }

    try {
      setSubmitting(true);
      await addDoc(collection(db, 'expenses'), {
        title: title.trim(),
        amount: parseFloat(amount),
        category,
        date,
        notes: notes.trim(),
        createdAt: serverTimestamp(),
      });

      setTitle('');
      setAmount('');
      setNotes('');
      setSubmitting(false);
      alert('खर्च यशस्वीपणे जमा केला!');
    } catch (err) {
      setSubmitting(false);
      alert('Error: ' + err.message);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (window.confirm('हा खर्च डिलीट करायचा आहे का?')) {
      try {
        await deleteDoc(doc(db, 'expenses', id));
      } catch (err) {
        alert('Error: ' + err.message);
      }
    }
  };

  // Metrics
  const totalExpense = expenses.reduce((acc, curr) => acc + (curr.amount || 0), 0);
  const totalCommissionRevenue = orders
    .filter(o => o.status === 'Delivered' || o.status === 'Completed')
    .reduce((acc, o) => acc + (o.commission || o.deliveryFee || 0), 0);
  const netProfit = totalCommissionRevenue - totalExpense;

  const filteredExpenses = expenses.filter(e => filterCat === 'All' || e.category === filterCat);

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', margin: '0 0 6px 0' }}>
          💰 Expense & Net Profit Tracker (खर्च व नफा हिशोब)
        </h2>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          अ‍ॅपचे होणारे सर्व खर्च ॲड करा आणि निव्वळ नफ्याची गणना (Net Profit) पहा.
        </div>
      </div>

      {/* Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card" style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.02))', borderColor: 'var(--danger)' }}>
          <div style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 700, marginBottom: 4 }}>एकूण खर्च (Total Expenses)</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--danger)' }}>₹{totalExpense.toLocaleString('en-IN')}</div>
        </div>

        <div className="card" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(59,130,246,0.02))', borderColor: 'var(--info)' }}>
          <div style={{ fontSize: 12, color: 'var(--info)', fontWeight: 700, marginBottom: 4 }}>एकूण कमिशन कमाई (Total Revenue)</div>
          <div style={{ fontSize: 24, fontWeight: 900, color: 'var(--info)' }}>₹{totalCommissionRevenue.toLocaleString('en-IN')}</div>
        </div>

        <div className="card" style={{
          background: netProfit >= 0 ? 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.02))' : 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.02))',
          borderColor: netProfit >= 0 ? 'var(--success)' : 'var(--danger)'
        }}>
          <div style={{ fontSize: 12, color: netProfit >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700, marginBottom: 4 }}>
            {netProfit >= 0 ? '📈 निव्वळ नफा (Net Profit)' : '📉 निव्वळ तोटा (Net Loss)'}
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            ₹{netProfit.toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20 }}>
        {/* Form to Add Expense */}
        <div className="card" style={{ height: 'fit-content' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconPlus size={18} /> नवीन खर्च जोडा (Add Expense)
          </h3>

          <form onSubmit={handleAddExpense} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>खर्चाचे नाव (Title)</label>
              <input
                type="text"
                placeholder="उदा. Google Server Charge"
                value={title}
                onChange={e => setTitle(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>रक्कम (Amount ₹)</label>
              <input
                type="number"
                placeholder="e.g. 1500"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>कॅटेगरी (Category)</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
              >
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>तारीख (Date)</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 700, display: 'block', marginBottom: 4 }}>टीप / माहिती (Notes - Optional)</label>
              <textarea
                placeholder="काही अतिरिक्त माहिती असल्यास..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', height: 60, resize: 'none' }}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={submitting} style={{ marginTop: 4 }}>
              {submitting ? 'जमा होत आहे...' : '💾 खर्च सेव्ह करा (Save Expense)'}
            </button>
          </form>
        </div>

        {/* Expense List */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>खर्चाची यादी (Expenses List)</h3>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                className={`btn ${filterCat === 'All' ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                onClick={() => setFilterCat('All')}
              >
                सर्व (All)
              </button>
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  className={`btn ${filterCat === c.id ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                  onClick={() => setFilterCat(c.id)}
                >
                  {c.id}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>लोड होत आहे...</div>
          ) : filteredExpenses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>कोणताही खर्च आढळला नाही.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left', color: 'var(--text-muted)' }}>
                    <th style={{ padding: '8px 12px' }}>तारीख</th>
                    <th style={{ padding: '8px 12px' }}>नाव / माहिती</th>
                    <th style={{ padding: '8px 12px' }}>कॅटेगरी</th>
                    <th style={{ padding: '8px 12px' }}>रक्कम (₹)</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map(exp => {
                    const catObj = CATEGORIES.find(c => c.id === exp.category) || CATEGORIES[5];
                    return (
                      <tr key={exp.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{exp.date}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <div style={{ fontWeight: 700 }}>{exp.title}</div>
                          {exp.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{exp.notes}</div>}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            background: `${catObj.color}15`, color: catObj.color, border: `1px solid ${catObj.color}40`
                          }}>
                            {catObj.label}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', fontWeight: 800, color: 'var(--danger)' }}>
                          ₹{exp.amount?.toLocaleString('en-IN')}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <button
                            onClick={() => handleDeleteExpense(exp.id)}
                            className="btn btn-danger btn-sm"
                            style={{ padding: '4px 8px' }}
                            title="Delete Expense"
                          >
                            <IconTrash size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
